import type { NoteFailurePresentedEventProps } from '@/models/analytics/features/notes';
import type {
  FrontendNoteFailureReason,
  QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  type GenerationCancellationReason,
  type GenerationHandle,
  type NoteFailedErrorCode,
} from '@heidi/session/data';

/** Capture once before attempt 1; reuse for every attempt without restoring the cache.
 * Keep this client-only snapshot scoped to one session and one retry invocation.
 */
export type GenerationAttemptBaseline = Readonly<
  Pick<
    QuerySessionResponse['consult_note'],
    | 'result'
    | 'status'
    | 'voice_style'
    | 'custom_style'
    | 'brain'
    | 'template_id'
    | 'ai_command_text'
    | 'generation_method'
  > & { content_type?: QuerySessionResponse['consult_note']['content_type'] }
>;

/** Copy only restore-owned fields, never backend identifiers or personalisation data. */
export function captureGenerationAttemptBaseline(
  note: GenerationAttemptBaseline
): GenerationAttemptBaseline {
  return {
    result: note.result,
    status: note.status,
    content_type: note.content_type,
    voice_style: note.voice_style,
    custom_style: note.custom_style,
    brain: note.brain,
    template_id: note.template_id,
    ai_command_text: note.ai_command_text,
    generation_method: note.generation_method,
  };
}

/** Client-only note failure reasons the commit holds instead of refetching. */
export type HeldNoteFailureReason =
  | FrontendNoteFailureReason.OFFLINE
  | FrontendNoteFailureReason.OUT_OF_PRO_ACTIONS
  | FrontendNoteFailureReason.STREAM_SERVICE_UNAVAILABLE;

export type PendingFailureCommit = {
  sessionId: string;
  baseline?: GenerationAttemptBaseline;
  previousResult: string | null | undefined;
  handle: GenerationHandle;
  error: unknown;
  /** Preserved through terminal deferral. */
  heldFeError?: HeldNoteFailureReason;
  /** Put the baseline note back, status included, instead of stamping FAILED.
   * Only set with a baseline, never with `heldFeError`. */
  restoreReadableNote?: true;
  /** Surface that told the user why the attempt was refused; absent means none. */
  recoverySurface?: NonNullable<NoteFailurePresentedEventProps['recovery_surface']>;
  /** Status the transport read off a non-2xx response. */
  httpStatus?: number;
  isHaikuIntegration?: true;
};

export type GenerationAttemptOutcome =
  | { kind: 'completed'; isEmpty: boolean }
  | { kind: 'delegated'; to: 'side-by-side' }
  | { kind: 'skipped'; reason: 'validation' | 'transcript-deleted' }
  | { kind: 'aborted'; reason: GenerationCancellationReason }
  | {
      kind: 'failed';
      /** pre-stream: initial token lookup; stream: onError; transport: setup or
       * transport rejection not delivered through onError (may be before open). */
      stage: 'pre-stream' | 'stream' | 'transport';
      httpStatus?: number;
      code: NoteFailedErrorCode;
      personalisationOwned: boolean;
      pendingCommit: PendingFailureCommit | null;
    };

export type AttemptOptions = {
  baseline?: GenerationAttemptBaseline;
  deferTerminalFailure: boolean;
  emptyCloseIsFailure: boolean;
};

export const DEFAULT_ATTEMPT_OPTIONS: AttemptOptions = {
  deferTerminalFailure: false,
  emptyCloseIsFailure: false,
};

export type GenerationAttempt = (
  options: AttemptOptions,
  attempt: 1 | 2
) => Promise<GenerationAttemptOutcome>;
